import { ReactNode } from 'react';
import { FormattedMessage, IntlProvider, MessageFormatElement } from 'react-intl';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { UnderlinedTextButton } from '../../../components/atoms/Button/TextButton';
import { DocumentOverlayLink } from '../../../components/screens/molecules/LegalDisclaimer/molecules/DocumentOverlayLink/DocumentOverlayLink';
import { PageView } from '../../../models/analytics';
import { showClientLegalDocs } from '../../../models/configuration';
import { getPrivacyPolicyUrl, getTermsAndConditionsUrl } from '../../../utilities/client-legal-url';

type Messages = Record<string, string> | Record<string, MessageFormatElement[]>;

const makeMessageKey = (clientId: string) => {
  return `ClientLegalDisclaimer.${clientId}`;
};

const clientLegalMessages: Messages = {
  // Stripe
  [`${makeMessageKey('1b8b46659d37404a9ce40526f9fcafcd')}`]:
    "By continuing and using the services, you agree that {clientName} may use your data to process your requested payment in accordance with {clientName}'s <terms>Terms</terms>. Your data will be processed according to {clientName}'s <privacy>Privacy Policy</privacy>. The following data will be shared with your merchant: last 4 digits of account number, sort code, and account holder name.",
  // Stripe-test-app
  [`${makeMessageKey('c99d6bbc9b624e778893534fadeb59f3')}`]:
    "By continuing and using the services, you agree that {clientName} may use your data to process your requested payment in accordance with {clientName}'s <terms>Terms</terms>. Your data will be processed according to {clientName}'s <privacy>Privacy Policy</privacy>. The following data will be shared with your merchant: last 4 digits of account number, sort code, and account holder name.",
  // Slimpay
  [`${makeMessageKey('6f447f6c96a34ca2a188b901343c6ff3')}`]:
    "Pour en savoir plus sur le service d'initiation de paiement, vous pouvez consulter la <terms>notice d'information</terms> ainsi que la <privacy>notice relative au traitement des données personnelles</privacy>.",
};

export enum LegalTextType {
  EXTERNAL = 'EXTERNAL',
  INTERNAL = 'INTERNAL',
}

type ClientLegalText = {
  type: LegalTextType;
  termsUrl: string;
  privacyUrl: string;
};

type LegalMapping = Record<string, ClientLegalText>;

export const clientMapping: LegalMapping = {
  // Stripe
  '1b8b46659d37404a9ce40526f9fcafcd': {
    type: LegalTextType.INTERNAL,
    termsUrl: getTermsAndConditionsUrl('1b8b46659d37404a9ce40526f9fcafcd'),
    privacyUrl: getPrivacyPolicyUrl('1b8b46659d37404a9ce40526f9fcafcd'),
  },
  // Stripe-test-app
  c99d6bbc9b624e778893534fadeb59f3: {
    type: LegalTextType.INTERNAL,
    termsUrl: getTermsAndConditionsUrl('c99d6bbc9b624e778893534fadeb59f3'),
    privacyUrl: getPrivacyPolicyUrl('c99d6bbc9b624e778893534fadeb59f3'),
  },
  // Slimpay
  '6f447f6c96a34ca2a188b901343c6ff3': {
    type: LegalTextType.EXTERNAL,
    termsUrl: 'https://www.slimpay.com/fr/notice-information/',
    privacyUrl: 'https://www.slimpay.com/fr/traitement-donnees-personnelles/',
  },
};

export type ShowClientLegalTextsArguments = {
  clientId: string;
  configuration: ConfigurationModel;
};

export const showClientLegalTexts = ({
  clientId,
  configuration,
}: ShowClientLegalTextsArguments) => {
  const featureFlag = showClientLegalDocs(configuration);
  const textExists = clientLegalMessages[`${makeMessageKey(clientId)}`];
  const mappingExists = Boolean(clientMapping[clientId]);

  return featureFlag && textExists && mappingExists;
};

type RichTextComponent = (chunks: ReactNode) => ReactNode;

type LegalTextProps = {
  clientName: string;
  clientId: string;
  Terms: RichTextComponent;
  Privacy: RichTextComponent;
};

const LegalText = ({ clientName, clientId, Terms, Privacy }: LegalTextProps) => {
  const intl = useIntl();

  const messages = {
    ...intl.messages,
    ...clientLegalMessages,
  } as Messages;

  return (
    <IntlProvider locale="en" messages={messages}>
      <FormattedMessage
        id={makeMessageKey(clientId)}
        values={{
          clientName,
          terms: Terms,
          privacy: Privacy,
        }}
      />
    </IntlProvider>
  );
};

type MakeExternalLinkArguments = {
  href: string;
  testId: string;
};

export const makeExternalLink = ({ href, testId }: MakeExternalLinkArguments) => {
  return (text: ReactNode) => (
    <UnderlinedTextButton
      as="a"
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noopener"
      tabIndex={0}
    >
      {text}
    </UnderlinedTextButton>
  );
};

type MakeInternalLinkArguments = {
  href: string;
  testId: string;
  pageView: PageView;
};

export const makeInternalLink = ({ href, testId, pageView }: MakeInternalLinkArguments) => {
  return (text: ReactNode) => (
    <DocumentOverlayLink testId={testId} documentUrl={href} pageView={pageView}>
      {text}
    </DocumentOverlayLink>
  );
};

export type ClientLegalDisclaimerProps = {
  clientId: string;
  clientName: string;
};

export const ClientLegalDisclaimer = ({ clientName, clientId }: ClientLegalDisclaimerProps) => {
  const clientConfig = clientMapping[clientId];

  if (clientConfig.type === LegalTextType.EXTERNAL) {
    return (
      <LegalText
        clientId={clientId}
        clientName={clientName}
        Terms={makeExternalLink({
          href: clientConfig.termsUrl,
          testId: 'external-terms-conditions-link',
        })}
        Privacy={makeExternalLink({
          href: clientConfig.privacyUrl,
          testId: 'external-privacy-link',
        })}
      />
    );
  }

  return (
    <LegalText
      clientId={clientId}
      clientName={clientName}
      Terms={makeInternalLink({
        href: clientConfig.termsUrl,
        testId: 'internal-terms-conditions-link',
        pageView: PageView.TERMS_AND_CONDITION_SCREEN,
      })}
      Privacy={makeInternalLink({
        href: clientConfig.privacyUrl,
        testId: 'internal-privacy-link',
        pageView: PageView.PRIVACY_POLICY_SCREEN,
      })}
    />
  );
};
